import React, { useState } from 'react';
import classnames from 'classnames';

import {
  Tooltip,
  Toolbar,
  Typography,
  Button,
  Paper,
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableContainer,
  TableSortLabel,
  TablePagination,
  TableCell as MuiTableCell
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import { Icon, Translate, SafeAnchor } from '@hydra/common/components';
import RankedKeywordsSearch from '@hydra/common/components/RankedKeywordsTable/RankedKeywordsSearch';
import RankedKeywordsFilter from '@hydra/common/components/RankedKeywordsTable/RankedKeywordsFilter';

import { sendEvent } from '@hydra/utils/gtm';
import { useI18n } from '@hydra/common/customHooks';

const INITIAL_PAGE = 0;
const INITIAL_ROWS_PER_PAGE = 20;

const TableCell = withStyles({
  root: {
    paddingTop: '10px',
    paddingBottom: '10px',
    whiteSpace: 'nowrap'
  }
})(MuiTableCell);

const useStyles = makeStyles(theme => ({
  addKeywordButton: {
    padding: '2px 10px',
    maxWidth: '100%'
  },
  table: {
    tableLayout: 'fixed'
  },
  keywordCell: {
    width: '30%',
    paddingRight: '0'
  },
  volumeCell: {
    width: '12%',
    padding: '0'
  },
  positionCell: {
    width: '10%'
  },
  urlCell: {
    width: '38%',
    paddingLeft: '0',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  keyword: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  cellLabel: {
    width: '100%'
  },
  leftToolbar: {
    flex: 1,
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold
  },
  rightToolbar: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  text: {
    color: theme.palette.grey[400],
    fontWeight: theme.typography.fontWeightSemibold
  },
  headerKeyword: {
    maxWidth: 80
  },
  volumeHeader: {
    maxWidth: 70
  },
  keywordFont: {
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold,
    lineHeight: `${theme.typography.fontSizeLarge}px`
  },
  toolbar: {
    paddingLeft: 0
  },
  tableRowHeight: {
    height: '50px'
  },
  emptyFilter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50px'
  }
}));

export const RankedKeywordsTable = (props) => {
  const classes = useStyles();
  const { numeral, translate } = useI18n();
  const { rankedKeywords, onClickKeyword, areKeywordsDisabled } = props;
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('volume');
  const [page, setPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);

  const [keywordFilter, setKeywordFilter] = useState('');
  const [volumeFilter, setVolumeFilter] = useState({ from: '', to: '' });
  const [positionFilter, setPositionFilter] = useState({ from: '', to: '' });
  const [urlFilter, setUrlFilter] = useState('');

  const getSorting = (order, orderBy) => {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
  };

  const stableSort = (array, cmp) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  };

  const desc = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const labelDisplayedRows = ({ from, to, count }) => {
    return `${from}-${to === -1 ? count : to} ${translate('serp_table_of')} ${count}`;
  };

  const filterKeywords = (arr) => {
    let filteredKeywords = arr.map(x => x);
    if (keywordFilter) {
      filteredKeywords = filteredKeywords.filter(({ keyword }) => keyword.includes(keywordFilter.toLowerCase()));
    }
    if (volumeFilter.from || volumeFilter.to) {
      const { from, to } = volumeFilter;
      filteredKeywords = filteredKeywords.filter(({ volume }) => {
        if (from && to) {
          return parseFloat(from) <= volume && volume <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= volume;
        } else {
          return volume <= parseFloat(to);
        }
      });
    }
    if (positionFilter.from || positionFilter.to) {
      const { from, to } = positionFilter;
      filteredKeywords = filteredKeywords.filter(({ position }) => {
        if (from && to) {
          return parseFloat(from) <= position && position <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= position;
        } else {
          return position <= parseFloat(to);
        }
      });
    }
    if (urlFilter) {
      filteredKeywords = filteredKeywords.filter(({ url }) => url.includes(urlFilter.toLowerCase()));
    }

    return filteredKeywords;
  };

  const handleKeywordClick = e => {
    sendEvent('keywordtool-addkeyword-rankedkeywordadded-label');
    onClickKeyword(e.currentTarget.value);
  };

  const filteredRankedKeywords = filterKeywords(rankedKeywords);
  const showPagination = filteredRankedKeywords.length > INITIAL_ROWS_PER_PAGE;
  return (
    <>
      <Toolbar className={classes.toolbar}>
        <Translate component='div' content='serp_add_ranked_keywords' className={classes.leftToolbar} />
        <div className={classes.rightToolbar}>
          <RankedKeywordsSearch
            keywordFilter={keywordFilter}
            setKeywordFilter={setKeywordFilter}
          />
          <RankedKeywordsFilter
            volumeFilter={volumeFilter}
            setVolumeFilter={setVolumeFilter}
            positionFilter={positionFilter}
            setPositionFilter={setPositionFilter}
            urlFilter={urlFilter}
            setUrlFilter={setUrlFilter}
          />
        </div>
      </Toolbar>
      <Paper>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell
                  key='keyword'
                  sortDirection={orderBy === 'keywords' ? order : false}
                  className={classnames(
                    classes.keywordCell,
                    classes.headerKeyword
                  )}
                >
                  <TableSortLabel
                    active={orderBy === 'keyword'}
                    direction={orderBy === 'keyword' ? order : 'asc'}
                    onClick={createSortHandler('keyword')}
                    className={classes.cellLabel}
                  >
                    <Translate content='keywords' component='div' className={classes.text} />
                  </TableSortLabel>
                </TableCell>
                <Tooltip placement='top' title={translate('serp_front_volume_tooltip')}>
                  <TableCell
                    key='volume'
                    sortDirection={orderBy === 'volume' ? order : false}
                    align='right'
                    className={classnames(
                      classes.volumeCell,
                      classes.volumeHeader
                    )}
                  >
                    <TableSortLabel
                      active={orderBy === 'volume'}
                      direction={orderBy === 'volume' ? order : 'asc'}
                      onClick={createSortHandler('volume')}
                      className={classes.cellLabel}
                    >
                      <Translate content='serp_front_volume' component='div' className={classes.text} />
                    </TableSortLabel>
                  </TableCell>
                </Tooltip>
                <TableCell
                  key='position'
                  sortDirection={orderBy === 'position' ? order : false}
                  align='right'
                  className={classes.positionCell}
                >
                  <TableSortLabel
                    active={orderBy === 'position'}
                    direction={orderBy === 'position' ? order : 'asc'}
                    onClick={createSortHandler('position')}
                    className={classes.cellLabel}
                  >
                    <Translate content='serp_ranked_keywords_position' component='div' className={classes.text} />
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  key='url'
                  sortDirection={orderBy === 'url' ? order : false}
                  className={classes.urlCell}
                >
                  <TableSortLabel
                    active={orderBy === 'url'}
                    direction={orderBy === 'url' ? order : 'asc'}
                    onClick={createSortHandler('url')}
                    className={classes.cellLabel}
                  >
                    <Translate content='url' component='div' className={classes.text} />
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(filteredRankedKeywords, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(({ keyword, volume, position, path, url }) => {
                  return (
                    <TableRow key={keyword} className={classes.tableRowHeight}>
                      <TableCell className={classes.keywordCell}>
                        <Button
                          className={classes.addKeywordButton}
                          disabled={areKeywordsDisabled}
                          variant='outlined'
                          color='primary'
                          onClick={handleKeywordClick}
                          value={keyword}
                        >
                          <Icon
                            color={areKeywordsDisabled ? 'grey-medium' : 'primary'}
                            size='xs'
                            type='plus'
                          />
                          <div
                            className={classnames(
                              classes.keyword,
                              classes.keywordFont
                            )}
                          >
                            {keyword}
                          </div>
                        </Button>
                      </TableCell>
                      <TableCell
                        align='right'
                        className={classes.volumeCell}
                      >
                        {volume ? numeral(volume).format('0.[00]a') : '-'}
                      </TableCell>
                      <TableCell
                        align='right'
                        className={classes.positionCell}
                      >
                        {position}
                      </TableCell>
                      <TableCell className={classes.urlCell}>
                        <SafeAnchor
                          href={url}
                          rel='nofollow noopener noreferrer'
                          target='_blank'
                        >
                          <Typography noWrap>
                            {path}
                          </Typography>
                        </SafeAnchor>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 25, 50, 100]}
            component='div'
            count={filteredRankedKeywords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage=''
            labelDisplayedRows={labelDisplayedRows}
          />
        )}
      </Paper>
      {
        filteredRankedKeywords.length === 0 && (
          <Paper className={classes.emptyFilter}>
            <Translate content='serp_ranked_keywords_filter_empty_state' />
          </Paper>
        )
      }
    </>
  );
};

export default RankedKeywordsTable;
